import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { VcenterSpecifics, ViewTypes } from '../types';
import { translateFn } from '../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { GzSingleSelectComplexWebComponent } from '../../../../ui/gzSingleSelectComplex';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { API_TYPE_COMPANIES_FOR_VMWARE_INTEGRATIONS } from '../../../../webcomponents.constants';
import { GetItemsResponse } from '../../../../ui/gzSingleSelectComplex/types';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { IntegrationActionContext } from '../../../integrationsMarketplaceGridInstalled/types';
import { GzCheckboxWebComponent } from '../../../../ui/gzCheckbox';
import { GzTextWebComponent } from '../../../../ui/gzText';
import { GzInputNumberWebComponent, InputWidthValues } from '../../../../ui/gzInput';
import { GzRadioGroupWebComponent, GzRadioWebComponent } from '../../../../ui/gzRadio';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import { MinLengthValidator } from '../../../../ui/gzForm/validators/min-length.validator';
import { MaxLengthValidator } from '../../../../ui/gzForm/validators/max-length.validator';
import { RegexValidator } from '../../../../ui/gzForm/validators/regex.validator';
import { IntegrationConstraints } from '../../../constraints/IntegrationConstraints';
import { getIntegrationStrings } from '../../../integrationStrings/integrationStrings';
import { ValidationResult } from '../../../../ui/gzForm/formField/types/form-validator';
import { GzInputWebComponent } from '../../../../ui/gzInput/component/GzInputWebComponent';
import { MinValidator } from '../../../../ui/gzForm/validators/min.validator';
import { MaxValidator } from '../../../../ui/gzForm/validators/max.validator';
import { GzToggleWebComponent } from '../../../../ui/gzToggle';
import { GzTitleWebComponent } from '../../../../ui/gzTitle';
import { INTEGRATION_TYPES } from '../../../common/constants';
import { RemoteValidator } from '../../../../ui/gzForm/validators/remote.validator';
import { Company, IntegrationData } from '../../types';

export class VcenterView extends BaseWebComponentView {
  public context: IntegrationActionContext;
  public formGroup: FormGroup;
  public dom: {
    $integrationName: GzFormFieldWebComponent;
    $hostName: GzFormFieldWebComponent;
    $port: GzFormFieldWebComponent;
    $username: GzFormFieldWebComponent;
    $password: GzFormFieldWebComponent<GzInputWebComponent, string>;

    $companyFormField: GzFormFieldWebComponent<GzSingleSelectComplexWebComponent, string | number>;

    $customizeSyncCheckbox: GzCheckboxWebComponent;
    $customizeSyncDescription: GzTextWebComponent;
    $retryAuthenticationTimeToWait: GzInputNumberWebComponent;
    $retryAuthenticationMaxAttempts: GzInputNumberWebComponent;
    $verifyTLSCertificate: GzToggleWebComponent;
    $verifyTLSCertificateDescription: GzTextWebComponent;

    $viewType: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;
    $viewTypeGroup: GzRadioGroupWebComponent;
    $viewTypeHostsAndClusters: GzRadioWebComponent;
    $viewTypeVmsAndTemplates: GzRadioWebComponent;
    $viewTypeDescription: GzTextWebComponent;

    $detailsSectionTitle: GzTitleWebComponent;
    $retryAuthenticationTimeToWaitLabel: GzTextWebComponent;
    $retryAuthenticationMaxAttemptsLabel: GzTextWebComponent;
    $integrationDescription: GzTextWebComponent;
  };
  private company: Company | undefined;

  getDomElements() {
    this.dom = {
      $integrationName: this.shadow.querySelector('#integration-name'),
      $hostName: this.shadow.querySelector('#hostname'),
      $port: this.shadow.querySelector('#port'),
      $username: this.shadow.querySelector('#username'),
      $password: this.shadow.querySelector('#password'),
      $companyFormField: this.shadow.querySelector('#integration-company-form-field'),
      $customizeSyncCheckbox: this.shadow.querySelector('#customize-sync-checkbox'),
      $customizeSyncDescription: this.shadow.querySelector('#customize-sync-description'),
      $retryAuthenticationTimeToWait: this.shadow.querySelector('#retry-interval'),
      $retryAuthenticationMaxAttempts: this.shadow.querySelector('#max-tries'),
      $verifyTLSCertificate: this.shadow.querySelector('#use-ssl'),
      $verifyTLSCertificateDescription: this.shadow.querySelector('#use-ssl-description'),

      $viewType: this.shadow.querySelector('#view-type'),
      $viewTypeGroup: this.shadow.querySelector('#view-type-group'),
      $viewTypeHostsAndClusters: this.shadow.querySelector('#view-type-hosts-and-clusters'),
      $viewTypeVmsAndTemplates: this.shadow.querySelector('#view-type-vms-and-templates'),
      $viewTypeDescription: this.shadow.querySelector('#view-type-description'),

      $detailsSectionTitle: this.shadow.querySelector('#details-section-title'),

      $retryAuthenticationTimeToWaitLabel: this.shadow.querySelector('#retry-interval-label'),
      $retryAuthenticationMaxAttemptsLabel: this.shadow.querySelector('#max-tries-label'),

      $integrationDescription: this.shadow.querySelector('#integration-description'),
    };

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations() {
    this.translations = getIntegrationStrings();
    return this.translations;
  }

  async buildSuccessView(viewData: any): Promise<void> {
    this.setCompanyDropdown(viewData);

    // Checkbox event listener
    this.dom.$customizeSyncCheckbox.addEventListener('change', () => {
      this.dom.$retryAuthenticationTimeToWait.disabled = !this.dom.$customizeSyncCheckbox.checked;
      this.dom.$retryAuthenticationMaxAttempts.disabled = !this.dom.$customizeSyncCheckbox.checked;
    });

    this.dom.$customizeSyncCheckbox.setFormValue(true);
    this.dom.$verifyTLSCertificate.setFormValue(true);

    // Additional setup
    this.createFormGroup(viewData);
    this.initViewTypes();
  }

  setTranslatedStrings(): void {
    this.dom.$integrationName.label = this.translations.integrationNameLabel;
    this.dom.$hostName.label = this.translations.hostNameLabel;
    this.dom.$port.label = this.translations.portLabel;
    this.dom.$port.value = 443;
    this.dom.$username.label = this.translations.usernameLabel;
    this.dom.$password.label = this.translations.passwordLabel;
    this.dom.$password.Input.placeholder = this.translations.passwordPlaceHolder;

    this.dom.$detailsSectionTitle.innerText = this.dependencies.translateFn('Configuration');

    this.dom.$companyFormField.label = this.dependencies.translateFn('Company');
    if (this.dom.$companyFormField.Input) {
      this.dom.$companyFormField.Input.placeholder = this.dependencies.translateFn('Choose company');
      this.dom.$companyFormField.Input.searchPlaceholderString = this.dependencies.translateFn('Search');
      this.dom.$companyFormField.Input.noSearchResultsString = this.dependencies.translateFn('No options found');
    }
    this.dom.$customizeSyncCheckbox.label = translateFn('Customize synchronizations with authentication requests');
    this.dom.$customizeSyncDescription.innerText = translateFn(
      'Use this option to change the synchronization interval in case of authentication failure. If the set limits are exceeded, the account is locked out. For security reasons, it is recommended to allow three authentication attempts at an interval of five minutes.'
    );

    this.dom.$retryAuthenticationTimeToWaitLabel.innerText = translateFn('Time to wait(minutes):');
    this.dom.$retryAuthenticationTimeToWait.inputWidth = InputWidthValues.SMALL;
    this.dom.$retryAuthenticationTimeToWait.min = 1;
    this.dom.$retryAuthenticationTimeToWait.value = 5;
    this.dom.$retryAuthenticationTimeToWait.max = 60;
    this.dom.$retryAuthenticationTimeToWait.disabled = true;

    this.dom.$retryAuthenticationMaxAttemptsLabel.innerText = translateFn('Number attempts:');
    this.dom.$retryAuthenticationMaxAttempts.inputWidth = InputWidthValues.SMALL;
    this.dom.$retryAuthenticationMaxAttempts.min = 1;
    this.dom.$retryAuthenticationMaxAttempts.value = 3;
    this.dom.$retryAuthenticationMaxAttempts.max = 100;
    this.dom.$retryAuthenticationMaxAttempts.disabled = true;

    this.dom.$verifyTLSCertificate.innerHTML = translateFn('Verify certificate');
    this.dom.$verifyTLSCertificateDescription.innerText = translateFn(
      'When enabled, by default, the Integrator will use Windows Trusted Root Certification Authorities to validate the vCenter certificate. Disabling this feature will cause the Integrator to ignore certificate errors.'
    );

    this.dom.$viewTypeHostsAndClusters.label = translateFn('Hosts and Clusters');
    this.dom.$viewTypeVmsAndTemplates.label = translateFn('VMs and Templates');
    this.dom.$viewTypeDescription.innerText = translateFn('Choose which vCenter view to sync:');

    this.dom.$viewTypeVmsAndTemplates.value = ViewTypes.VmsAndTemplates;
    this.dom.$viewTypeHostsAndClusters.value = ViewTypes.HostsAndClusters;

    this.dom.$integrationDescription.innerText = translateFn(
      'Configuring this integration allows you to connect GravityZone Cloud to VMware vCenter via an endpoint which has the BEST agent installed. This assigned endpoint enables the detection and synchronization of the current inventory.'
    );
  }

  public createFormGroup(viewData: any): void {
    this.formGroup = new FormGroup(this.getFormGroupItems());
    this.setFormFieldsValidators(viewData);
  }

  public getFormGroup(): FormGroup {
    return this.formGroup;
  }

  public setPasswordValidator(integrationId: string): void {
    //validate only if integration is new
    if (!integrationId) {
      this.dom.$password.Validators = [RequiredValidator(this.commonStrings.requiredField)];
    }
  }

  public setIntegrationData(integration: IntegrationData<VcenterSpecifics>) {
    this.dom.$companyFormField.Input.setFormValue(integration.company.id);
    this.dom.$integrationName.Input.setFormValue(integration.name);
    this.dom.$hostName.Input.setFormValue(integration.specifics.hostname);
    this.dom.$port.Input.setFormValue(integration.specifics.port);
    this.dom.$username.Input.setFormValue(integration.specifics.username);

    this.dom.$password.Input.setFormValue(integration.specifics.password);
    if (integration._id) {
      this.dom.$password.Input.setFormValue('');
    }

    this.dom.$customizeSyncCheckbox.setFormValue(integration.specifics.retryAuthentication);
    //disable retryAuthenticationTimeToWait and retryAuthenticationMaxAttempts if customizeSyncCheckbox is not checked
    this.dom.$retryAuthenticationTimeToWait.disabled = !integration.specifics.retryAuthentication;
    this.dom.$retryAuthenticationMaxAttempts.disabled = !integration.specifics.retryAuthentication;
    this.dom.$retryAuthenticationTimeToWait.value = integration.specifics.retryAuthenticationTimeToWait;
    this.dom.$retryAuthenticationMaxAttempts.value = integration.specifics.retryAuthenticationMaxAttempts;
    this.dom.$verifyTLSCertificate.setFormValue(integration.specifics.verifyTLSCertificate);
    this.dom.$viewType.Input.setFormValue(integration.specifics.viewType);
  }

  private resetData(): void {
    if (!this.formGroup) {
      return;
    }

    this.formGroup.applyToFormGroupFields(field => {
      if (field instanceof GzFormFieldWebComponent) {
        field.discardChanges();
        field.resetField();
      }
    });
  }

  private setFormFieldsValidators(viewData: any): void {
    this.setIntegrationNameValidator(viewData.integrationId);
    this.setUsernameValidator();
    this.setPasswordValidator(viewData.integrationId);
    this.setPortValidator();
    this.setIpHostnameValidator();
    this.setCompanyValidators();
  }

  private setCompanyValidators(): void {
    this.dom.$companyFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];
  }

  private setUsernameValidator(): void {
    this.dom.$username.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MinLengthValidator(IntegrationConstraints.IntegrationName.MinLength, this.translations.userNameMinLength),
      MaxLengthValidator(IntegrationConstraints.IntegrationName.MaxLength, this.translations.userNameMaxLength),
    ];
  }

  private setPortValidator(): void {
    this.dom.$port.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      RegexValidator(/^\d+$/, this.translations.invalidPort),
      MinValidator(1, this.translations.invalidPort),
      MaxValidator(65535, this.translations.invalidPort),
    ];
  }

  private setIntegrationNameValidator(integrationId: string): void {
    this.dom.$integrationName.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(128, this.translations.integrationNameMaxLength),
      RegexValidator(
        /^[^\s](.)*$/, // ensures no leading whitespace
        this.translations.invalidIntegrationName
      ),
      RemoteValidator(
        (value: string) =>
          this.dependencies.api.clients.IntegrationsMarketplace.CheckIntegrationNameIsUnique(
            value,
            this.dom.$companyFormField?.value || this.dependencies.getActiveCompany()?.companyId,
            integrationId,
            INTEGRATION_TYPES.INTEGRATION_TYPE_VMWARE
          ),
        (response: { success: boolean }) => response.success,
        this.dependencies.translateFn('Integration name already exists in the company.'),
        this.dependencies.translateFn('A communication error has occurred. Please enter the integration name again.')
      ),
    ];
  }

  private setIpHostnameValidator(): void {
    const ipv4Pattern =
      /^(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])$/;

    const hostnamePattern = /^(?!-)[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*$/; // Supports internal (e.g., localhost) and external hostnames.

    const combinedPattern = new RegExp(`^(${ipv4Pattern.source}|${hostnamePattern.source})$`);

    this.dom.$hostName.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      RegexValidator(combinedPattern, this.translations.invalidHostname),
    ];
  }

  private async customMinLengthValidation(): Promise<ValidationResult> {
    const validator = MinLengthValidator(
      IntegrationConstraints.Password.MinLength,
      this.translations.passwordMinLength
    );
    return await validator(this.dom.$password.value);
  }

  private setCompanyDropdown(viewData: any): void {
    if (this.dependencies.permissions.canHaveChildCompanies) {
      toggleVisibility(this.dom.$companyFormField, true);
      this.dom.$companyFormField.removeAttribute('disabled');

      this.dom.$companyFormField.Input.setApiType(API_TYPE_COMPANIES_FOR_VMWARE_INTEGRATIONS);

      const companyData: GetItemsResponse = {
        items: [],
        totalItemsCount: 0,
      };

      let edit = false;
      if (viewData.company) {
        companyData.items.push({
          label: viewData.company.name,
          value: viewData.company?.id,
        });
        companyData.totalItemsCount = 1;
        edit = true;
      }

      this.dom.$companyFormField.Input.updateComplete.then(() => {
        // set preventBackendCall flag in context of Edit because there is no need to load companies from db
        this.dom.$companyFormField.Input.controller.setFilterOptions(companyData, edit).then().catch();
      });
      if (edit) {
        this.dom.$companyFormField.setAttribute('disabled', '');
      }
    } else {
      toggleVisibility(this.dom.$companyFormField, false);
      this.dom.$companyFormField.setAttribute('disabled', '');
    }
  }

  private getFormGroupItems() {
    const formGroupItems: {} = {};

    formGroupItems['integrationName'] = this.dom.$integrationName;
    formGroupItems['hostName'] = this.dom.$hostName;
    formGroupItems['port'] = this.dom.$port;
    formGroupItems['username'] = this.dom.$username;
    formGroupItems['password'] = this.dom.$password;
    formGroupItems['company'] = this.dom.$companyFormField;

    return formGroupItems;
  }

  private initViewTypes(): void {
    this.dom.$viewTypeHostsAndClusters.value = ViewTypes.HostsAndClusters;
    this.dom.$viewTypeVmsAndTemplates.value = ViewTypes.VmsAndTemplates;

    // set default
    this.dom.$viewType.Input.setFormValue(ViewTypes.VmsAndTemplates);
  }
}
